import { useEffect, useMemo, useRef, useState } from "react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { API_BASE, ApiError, api } from "@/lib/apiClient";
import {
  billingIntervalFromCategory,
  billingIntervalLabel,
  billingPeriodAmountLabel,
  contractEndForStart,
  periodInvoiceButtonLabel,
} from "@/lib/contractBilling";
import { formatIsoDateShort, todayLocal } from "@/lib/bookingDates";
import { formatCurrency } from "@/lib/money";
import { canWriteInvoice } from "@/lib/permissions";
import { contractTypeLabel } from "@/lib/contractType";
import { normalizeRentalPeriod, rentalPeriodPriceLabel } from "@/lib/rentalPeriod";
import { tenantSlug } from "@/lib/tenantSlug";
import { usePitchCategories } from "@/lib/usePitchCategories";
import { RegisterVisitorModal } from "@/pages/visitors/RegisterVisitorModal";
import {
  ContractChoice,
  ContractCreditBadge,
  ContractFact,
  ContractStatusBadge,
  ContractTypeBadge,
  contractCreditState,
  contractIsOpen,
} from "@/pages/contracts/contractDisplay";
import type { BillingInterval, Contract, ContractStatus, Invoice, Pitch } from "@/types/camping";

const MAX_SCAN_BYTES = 12 * 1024 * 1024;

export interface EditContractModalProps {
  open: boolean;
  contract: Contract | null;
  onClose: () => void;
  onSaved: () => void;
  canEdit: boolean;
  userGroups: string[];
}

function amountTitle(interval: BillingInterval): string {
  return billingPeriodAmountLabel(interval).replace(" (EUR, brutto)", "");
}

function decimalInput(value: string): string {
  const trimmed = value.trim().replace(/\s/g, "");
  if (trimmed.includes(",") && !trimmed.includes(".")) return trimmed.replace(",", ".");
  return trimmed;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toLocaleString("de-DE", { maximumFractionDigits: 1 })} MB`;
}

async function openContractScan(contractId: string): Promise<void> {
  const path = `/api/v1/contracts/${contractId}/scan`;
  const url = API_BASE ? `${API_BASE}${path}` : path;
  const res = await fetch(url, {
    credentials: "include",
    headers: { "X-Tenant-Slug": tenantSlug() },
  });
  if (!res.ok) {
    throw new ApiError(res.status, "Der Vertragsscan konnte nicht geöffnet werden.");
  }
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const opened = window.open(objectUrl, "_blank", "noopener");
  if (!opened) {
    const link = document.createElement("a");
    link.href = objectUrl;
    link.download = "Vertragsscan";
    link.click();
  }
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
}

export function EditContractModal({
  open,
  contract,
  onClose,
  onSaved,
  canEdit,
  userGroups,
}: EditContractModalProps) {
  const [noticePeriod, setNoticePeriod] = useState(90);
  const [autoRenew, setAutoRenew] = useState(false);
  const [periodAmountGross, setPeriodAmountGross] = useState("");
  const [endDate, setEndDate] = useState("");
  const [status, setStatus] = useState<ContractStatus>("active");
  const [notes, setNotes] = useState("");
  const [specialTerms, setSpecialTerms] = useState("");
  const [cancellationReceived, setCancellationReceived] = useState("");
  const [cancellationEffective, setCancellationEffective] = useState("");
  const [cancellationNote, setCancellationNote] = useState("");
  const [scanFilename, setScanFilename] = useState<string | null>(null);
  const [scanSize, setScanSize] = useState<number | null>(null);
  const [scanUploadedAt, setScanUploadedAt] = useState<string | null>(null);
  const [pitch, setPitch] = useState<Pitch | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [creditLoading, setCreditLoading] = useState(false);
  const [creditInvoiceNumber, setCreditInvoiceNumber] = useState<string | null>(null);
  const [scanBusy, setScanBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [invoiceMessage, setInvoiceMessage] = useState<string | null>(null);
  const [visitorOpen, setVisitorOpen] = useState(false);
  const endBeforeCancel = useRef<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const { categories } = usePitchCategories(open && Boolean(contract));

  const canInvoice = canWriteInvoice(userGroups);

  useEffect(() => {
    if (!open || !contract) return;
    setNoticePeriod(contract.notice_period_days);
    setAutoRenew(contract.auto_renew);
    setPeriodAmountGross(
      contract.billing_period_amount_gross ?? contract.monthly_amount ?? "",
    );
    setEndDate(contract.end_date ?? "");
    setStatus(contract.status);
    setNotes(contract.notes ?? "");
    setSpecialTerms(contract.special_terms ?? "");
    setCancellationReceived(contract.cancellation_received_on ?? "");
    setCancellationEffective(contract.cancellation_effective_on ?? "");
    setCancellationNote(contract.cancellation_note ?? "");
    setScanFilename(contract.scan_filename ?? null);
    setScanSize(contract.scan_size_bytes ?? null);
    setScanUploadedAt(contract.scan_uploaded_at ?? null);
    setPitch(null);
    setError(null);
    setInvoiceMessage(null);
    setCreditInvoiceNumber(contract.replacement_credit_invoice_number ?? null);
    setSubmitting(false);
    endBeforeCancel.current = null;
  }, [open, contract]);

  useEffect(() => {
    if (!open || !contract) return;
    let cancelled = false;
    api
      .get<Pitch[]>("/api/v1/pitches")
      .then((list) => {
        if (!cancelled) setPitch(list.find((item) => item.id === contract.pitch_id) ?? null);
      })
      .catch(() => {
        if (!cancelled) setPitch(null);
      });
    return () => {
      cancelled = true;
    };
  }, [open, contract]);

  const category = useMemo(() => {
    const categoryId = pitch?.category_id;
    if (!categoryId) return null;
    return categories.find((item) => item.id === categoryId) ?? null;
  }, [categories, pitch]);

  const categoryInterval = billingIntervalFromCategory(category);
  const interval: BillingInterval = categoryInterval ?? contract?.billing_interval ?? "monthly";
  const categoryName = category?.name ?? pitch?.category_name ?? null;
  const categoryColor = category?.color ?? pitch?.category_color ?? null;
  const rentalPeriod = category ? normalizeRentalPeriod(category.rental_period) : null;
  const pauschaleLabel = amountTitle(interval);

  const headerAmount = periodAmountGross
    ? decimalInput(periodAmountGross)
    : contract?.resolved_period_amount_gross ||
      contract?.assignment_period_amount_gross ||
      contract?.billing_period_amount_gross ||
      contract?.monthly_amount ||
      "";
  const priceFromAssignment = !periodAmountGross && !contract?.billing_period_amount_gross && !contract?.monthly_amount;
  const headerHint = periodAmountGross || contract?.billing_period_amount_gross || contract?.monthly_amount
    ? "Abweichend für diesen Vertrag"
    : contract?.assignment_period_amount_gross || contract?.resolved_period_amount_gross
      ? "Aus der Zuordnung"
      : contract?.charge_rule_id
        ? "Aus der Zuordnung"
        : "Kein Preis hinterlegt";

  function onReceivedChange(value: string) {
    if (value && !cancellationReceived) {
      endBeforeCancel.current = endDate;
      setAutoRenew(false);
      const nextEffective = cancellationEffective || endDate;
      if (nextEffective) {
        setCancellationEffective(nextEffective);
        setEndDate(nextEffective);
      }
    }
    setCancellationReceived(value);
    if (!value) {
      setCancellationEffective("");
      setCancellationNote("");
      if (status === "notice") setStatus("active");
      if (endBeforeCancel.current != null && !contract?.cancellation_received_on) {
        setEndDate(endBeforeCancel.current);
      }
      if (!contract?.cancellation_received_on) setAutoRenew(contract?.auto_renew ?? false);
      endBeforeCancel.current = null;
    }
  }

  function onEffectiveChange(value: string) {
    setCancellationEffective(value);
    if (value) setEndDate(value);
  }

  function clearCancellation() {
    onReceivedChange("");
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!contract || !canEdit) return;
    if (cancellationReceived && !cancellationEffective) {
      setError("Das Wirksamkeitsdatum der Kündigung fehlt.");
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const payload: Record<string, unknown> = {
        notice_period_days: noticePeriod,
        auto_renew: cancellationReceived ? false : autoRenew,
        notes: notes.trim() || null,
        end_date: (cancellationReceived ? cancellationEffective : endDate) || null,
        special_terms: specialTerms.trim() || null,
        cancellation_received_on: cancellationReceived || null,
        cancellation_effective_on: cancellationReceived ? cancellationEffective : null,
        cancellation_note: cancellationReceived ? cancellationNote.trim() || null : null,
      };
      if (!cancellationReceived) payload.status = status === "notice" ? "active" : status;
      const amount = decimalInput(periodAmountGross);
      if (amount) payload.billing_period_amount_gross = amount;
      await api.patch<Contract>(`/api/v1/contracts/${contract.id}`, payload);
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Fehler beim Speichern");
    } finally {
      setSubmitting(false);
    }
  }

  async function createReplacementCredit() {
    if (!contract) return;
    setCreditLoading(true);
    setError(null);
    setInvoiceMessage(null);
    try {
      const inv = await api.post<Invoice>(`/api/v1/contracts/${contract.id}/replacement-credit`, {});
      setCreditInvoiceNumber(inv.invoice_number);
      setInvoiceMessage(`Gutschrift-Entwurf ${inv.invoice_number} angelegt.`);
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Gutschrift konnte nicht erzeugt werden");
    } finally {
      setCreditLoading(false);
    }
  }

  async function createMonthlyInvoice() {
    if (!contract) return;
    setInvoiceLoading(true);
    setInvoiceMessage(null);
    setError(null);
    try {
      const inv = await api.post<Invoice>(`/api/v1/contracts/${contract.id}/monthly-invoice`, {});
      setInvoiceMessage(`Rechnungsentwurf ${inv.invoice_number} angelegt.`);
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Rechnung konnte nicht erzeugt werden");
    } finally {
      setInvoiceLoading(false);
    }
  }

  function applyScan(updated: Contract) {
    setScanFilename(updated.scan_filename ?? null);
    setScanSize(updated.scan_size_bytes ?? null);
    setScanUploadedAt(updated.scan_uploaded_at ?? null);
  }

  async function uploadScan(file: File) {
    if (!contract) return;
    if (file.size > MAX_SCAN_BYTES) {
      setError("Der Scan darf höchstens 12 MB groß sein.");
      return;
    }
    const body = new FormData();
    body.append("file", file);
    setScanBusy(true);
    setError(null);
    try {
      const updated = await api.post<Contract>(
        `/api/v1/contracts/${contract.id}/scan`,
        undefined,
        { rawBody: body },
      );
      applyScan(updated);
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Der Scan konnte nicht gespeichert werden.");
    } finally {
      setScanBusy(false);
    }
  }

  async function removeScan() {
    if (!contract) return;
    setScanBusy(true);
    setError(null);
    try {
      const updated = await api.del<Contract>(`/api/v1/contracts/${contract.id}/scan`);
      applyScan(updated);
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Der Scan konnte nicht entfernt werden.");
    } finally {
      setScanBusy(false);
    }
  }

  if (!contract) return null;

  const running = contractIsOpen(contract.status);
  const showVisitor = canEdit && running && contract.contract_type === "permanent";
  const showInvoice = canInvoice && running && Boolean(contract.booking_id);
  const creditGross = contract.replacement_credit_gross;
  const creditDays = contract.replacement_credit_days ?? 0;
  const showCredit = contractCreditState(contract) != null;
  const creditAlreadyIssued = Boolean(
    contract.replacement_credit_invoice_id || creditInvoiceNumber,
  );
  const suggestedEnd = contractEndForStart(contract.start_date, interval);
  const shortStay = rentalPeriod === "night" || rentalPeriod === "week";
  const intervalHint =
    categoryInterval && categoryName
      ? `Aus der Stellplatzkategorie ${categoryName}`
      : shortStay
        ? `${categoryName ?? "Diese Kategorie"} wird ${rentalPeriodPriceLabel(rentalPeriod)} vermietet`
        : categoryName
          ? `Kategorie ${categoryName}`
          : "Kommt aus der Stellplatzkategorie des Platzes";
  const cancellationPending = Boolean(cancellationReceived);
  const cancellationStatus: ContractStatus =
    cancellationEffective && cancellationEffective < todayLocal() ? "terminated" : "notice";

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={contract.guest_name ?? "Vertrag"}
      description={`${contractTypeLabel(contract.contract_type)} · Platz ${contract.pitch_code ?? "—"}`}
      size="xl"
      footer={
        <div className="flex flex-wrap justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose} disabled={submitting}>
            Schließen
          </Button>
          {canEdit ? (
            <Button type="submit" form="contract-edit-form" loading={submitting}>
              Speichern
            </Button>
          ) : null}
        </div>
      }
    >
      <div className="space-y-5">
        <div className="flex flex-wrap items-center gap-2">
          <ContractStatusBadge status={cancellationPending ? cancellationStatus : contract.status} />
          <ContractTypeBadge type={contract.contract_type} />
          <ContractCreditBadge contract={contract} issued={creditAlreadyIssued} />
          {contract.booking_number ? (
            <span className="text-xs text-label-secondary">Buchung {contract.booking_number}</span>
          ) : (
            <span className="text-xs text-label-tertiary">Keine Buchung</span>
          )}
          {cancellationPending && cancellationStatus !== contract.status ? (
            <span className="text-xs text-label-tertiary">Wird beim Speichern übernommen</span>
          ) : null}
        </div>

        <div className="grid grid-cols-1 gap-px overflow-hidden rounded-xl bg-separator-opaque sm:grid-cols-3">
          <ContractFact label="Beginn" value={formatIsoDateShort(contract.start_date)} />
          <ContractFact
            label="Intervall"
            value={
              <span className="inline-flex items-center gap-2">
                {categoryColor ? (
                  <span
                    className="h-2 w-2 shrink-0 rounded-full"
                    style={{ backgroundColor: categoryColor }}
                    aria-hidden
                  />
                ) : null}
                {billingIntervalLabel(interval)}
              </span>
            }
            hint={intervalHint}
          />
          <ContractFact
            label="Preis"
            value={headerAmount ? formatCurrency(headerAmount) : priceFromAssignment ? "aus Zuordnung" : "kein Preis"}
            hint={headerHint}
          />
        </div>

        {showVisitor || showInvoice ? (
          <div className="flex flex-col gap-2">
            <div className="flex flex-wrap gap-2">
              {showVisitor ? (
                <Button type="button" variant="secondary" size="sm" onClick={() => setVisitorOpen(true)}>
                  Besucher anmelden
                </Button>
              ) : null}
              {showInvoice ? (
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  loading={invoiceLoading}
                  onClick={() => void createMonthlyInvoice()}
                >
                  {periodInvoiceButtonLabel(interval)}
                </Button>
              ) : null}
            </div>
            <p className="text-xs text-label-secondary">
              {showVisitor
                ? "Besucher können sofort kassiert oder auf die Abrechnung gebucht werden. "
                : ""}
              {showInvoice ? "Die Rechnung entsteht als Entwurf." : ""}
            </p>
            {invoiceMessage ? <p className="text-sm text-status-green">{invoiceMessage}</p> : null}
          </div>
        ) : null}

        {showCredit ? (
          <section className="space-y-2 rounded-xl border border-separator bg-fill-tertiary/40 p-4">
            <h3 className="text-sm font-semibold text-label">Ablösung</h3>
            <p className="text-sm text-label-secondary">
              Dieser gekündigte Vertrag wurde zum {formatIsoDateShort(contract.superseded_on ?? "")}{" "}
              durch einen neuen Vertrag auf dem Platz abgelöst. Die Resttage vom{" "}
              {formatIsoDateShort(contract.replacement_credit_from ?? contract.superseded_on ?? "")} bis{" "}
              {formatIsoDateShort(contract.replacement_credit_until ?? contract.end_date ?? "")} ergeben{" "}
              {creditDays} {creditDays === 1 ? "Tag" : "Tage"} und{" "}
              {formatCurrency(creditGross)} brutto.
            </p>
            {creditAlreadyIssued ? (
              <p className="text-sm text-status-green">
                Gutschrift{" "}
                {creditInvoiceNumber || contract.replacement_credit_invoice_number || ""} wurde angelegt.
              </p>
            ) : canInvoice ? (
              <Button
                type="button"
                size="sm"
                loading={creditLoading}
                onClick={() => void createReplacementCredit()}
              >
                Gutschrift auslösen
              </Button>
            ) : (
              <p className="text-xs text-label-tertiary">
                Die Gutschrift kann von Rezeption oder Buchhaltung ausgelöst werden.
              </p>
            )}
          </section>
        ) : null}

        {error ? <p className="text-sm text-status-red">{error}</p> : null}

        <form
          id="contract-edit-form"
          onSubmit={(e) => void onSubmit(e)}
          className="overflow-hidden rounded-xl border border-separator"
        >
          <section className="space-y-3 p-4">
            <div>
              <h3 className="text-sm font-semibold text-label">Vertragsscan</h3>
              <p className="mt-0.5 text-xs text-label-secondary">
                PDF oder Bild des unterschriebenen Vertrags, höchstens 12 MB.
              </p>
            </div>
            {scanFilename ? (
              <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-separator bg-card px-3 py-2.5">
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-label">{scanFilename}</p>
                  <p className="text-xs text-label-tertiary">
                    {scanSize != null ? formatFileSize(scanSize) : "Scan"}
                    {scanUploadedAt ? ` · ${formatIsoDateShort(scanUploadedAt.slice(0, 10))}` : ""}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    disabled={scanBusy}
                    onClick={() => {
                      void openContractScan(contract.id).catch((err: unknown) => {
                        setError(err instanceof Error ? err.message : "Der Scan konnte nicht geöffnet werden.");
                      });
                    }}
                  >
                    Öffnen
                  </Button>
                  {canEdit ? (
                    <>
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        loading={scanBusy}
                        onClick={() => fileRef.current?.click()}
                      >
                        Ersetzen
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        disabled={scanBusy}
                        onClick={() => void removeScan()}
                      >
                        Entfernen
                      </Button>
                    </>
                  ) : null}
                </div>
              </div>
            ) : canEdit ? (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                loading={scanBusy}
                onClick={() => fileRef.current?.click()}
              >
                Scan hinterlegen
              </Button>
            ) : (
              <p className="text-sm text-label-secondary">Kein Scan hinterlegt.</p>
            )}
            <input
              ref={fileRef}
              type="file"
              accept="application/pdf,image/jpeg,image/png,image/webp,.pdf,.jpg,.jpeg,.png,.webp"
              className="hidden"
              aria-label="Vertragsscan auswählen"
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = "";
                if (file) void uploadScan(file);
              }}
            />
          </section>

          <section className="space-y-3 border-t border-separator p-4">
            <h3 className="text-sm font-semibold text-label">Laufzeit</h3>
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="block space-y-1 text-sm">
                <span className="text-label-secondary">Vertragsende</span>
                <input
                  type="date"
                  className="eh-input w-full tabular-nums"
                  value={endDate}
                  min={contract.start_date}
                  disabled={!canEdit || cancellationPending}
                  onChange={(e) => setEndDate(e.target.value)}
                />
                <p className="text-xs text-label-tertiary">
                  {cancellationPending
                    ? "Folgt dem Wirksamkeitsdatum der Kündigung."
                    : `Vorschlag zur Abrechnung: ${suggestedEnd}`}
                </p>
              </label>
              <label className="block space-y-1 text-sm">
                <span className="text-label-secondary">Kündigungsfrist (Tage)</span>
                <input
                  type="number"
                  min={0}
                  className="eh-input w-full"
                  value={noticePeriod}
                  disabled={!canEdit}
                  onChange={(e) => setNoticePeriod(Number(e.target.value) || 0)}
                />
              </label>
            </div>
            <ContractChoice
              checked={cancellationPending ? false : autoRenew}
              onChange={setAutoRenew}
              disabled={!canEdit || cancellationPending}
              title="Automatisch verlängern"
              hint={
                cancellationPending
                  ? "Ausgeschaltet, solange eine Kündigung hinterlegt ist."
                  : "Nach dem Vertragsende um einen Abrechnungszeitraum."
              }
            />
          </section>

          <section className="space-y-3 border-t border-separator p-4">
            <div>
              <h3 className="text-sm font-semibold text-label">Abrechnung</h3>
              <p className="mt-0.5 text-xs text-label-secondary">
                Das Intervall folgt der Stellplatzkategorie und wird hier nicht geändert.
              </p>
            </div>
            <div className="rounded-lg border border-separator bg-card-elevated px-3 py-2.5">
              <p className="text-xs text-label-tertiary">{pauschaleLabel} aus der Zuordnung</p>
              <p className="mt-0.5 text-sm font-medium tabular-nums text-label">
                {contract.assignment_period_amount_gross
                  ? formatCurrency(contract.assignment_period_amount_gross)
                  : contract.assignment_period_amount_gross === null
                    ? "Keine Pauschale aus einer Zuordnung"
                    : "aus Zuordnung"}
              </p>
              <p className="mt-0.5 text-xs text-label-secondary">
                {contract.assignment_period_amount_gross
                  ? "Brutto für einen Abrechnungszeitraum, aus dem Artikelpreis der Buchungszuordnung."
                  : contract.assignment_period_amount_gross === null
                    ? "Ohne Zuordnung gilt nur die Pauschale, die direkt am Vertrag steht."
                    : "Brutto für einen Abrechnungszeitraum aus der Buchungszuordnung."}
              </p>
            </div>
            <label className="block space-y-1 text-sm">
              <span className="text-label-secondary">
                Abweichende {pauschaleLabel} nur für diesen Vertrag (EUR, brutto)
              </span>
              <input
                type="text"
                inputMode="decimal"
                className="eh-input w-full"
                value={periodAmountGross}
                disabled={!canEdit}
                onChange={(e) => setPeriodAmountGross(e.target.value)}
                placeholder="Leer lassen, es gilt die Zuordnung"
              />
            </label>
          </section>

          <section className="space-y-3 border-t border-separator p-4">
            <div>
              <h3 className="text-sm font-semibold text-label">Sonderkonditionen</h3>
              <p className="mt-0.5 text-xs text-label-secondary">
                Gelten nur für diesen Vertrag und erscheinen in der Vertragsliste.
              </p>
            </div>
            <textarea
              className="eh-input w-full min-h-[4.5rem]"
              value={specialTerms}
              maxLength={4000}
              disabled={!canEdit}
              onChange={(e) => setSpecialTerms(e.target.value)}
              placeholder="z. B. Strom inklusive, Hund frei oder eine abweichende Nutzung"
            />
          </section>

          <section className="space-y-3 border-t border-separator p-4">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <h3 className="text-sm font-semibold text-label">Kündigung</h3>
                <p className="mt-0.5 text-xs text-label-secondary">
                  Bis zum Wirksamkeitsdatum bleibt der Vertrag gekündigt und läuft aus. Danach ist er
                  beendet. Die automatische Verlängerung wird ausgeschaltet.
                </p>
              </div>
              {canEdit && cancellationPending ? (
                <Button type="button" variant="ghost" size="sm" onClick={clearCancellation}>
                  Kündigung entfernen
                </Button>
              ) : null}
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="block space-y-1 text-sm">
                <span className="text-label-secondary">Eingegangen am</span>
                <input
                  type="date"
                  className="eh-input w-full tabular-nums"
                  value={cancellationReceived}
                  disabled={!canEdit}
                  onChange={(e) => onReceivedChange(e.target.value)}
                />
              </label>
              <label className="block space-y-1 text-sm">
                <span className="text-label-secondary">Wirksam zum</span>
                <input
                  type="date"
                  className="eh-input w-full tabular-nums"
                  value={cancellationEffective}
                  min={contract.start_date}
                  disabled={!canEdit || !cancellationPending}
                  onChange={(e) => onEffectiveChange(e.target.value)}
                />
                <p className="text-xs text-label-tertiary">
                  In der Regel das bestehende Vertragsende, wenn die Frist eingehalten ist.
                </p>
              </label>
            </div>
            <label className="block space-y-1 text-sm">
              <span className="text-label-secondary">Hinweis zur Kündigung</span>
              <textarea
                className="eh-input w-full min-h-[3.5rem]"
                value={cancellationNote}
                maxLength={2000}
                disabled={!canEdit || !cancellationPending}
                onChange={(e) => setCancellationNote(e.target.value)}
                placeholder="z. B. schriftlich eingegangen"
              />
            </label>
            {cancellationPending ? (
              <p className="text-xs text-status-orange">
                Beim Speichern wird der Status auf {cancellationStatus === "notice" ? "Gekündigt" : "Beendet"} gesetzt
                und der Vertrag verlängert sich nicht mehr.
              </p>
            ) : null}
          </section>

          <section className="space-y-3 border-t border-separator p-4">
            <h3 className="text-sm font-semibold text-label">Status und Notizen</h3>
            {cancellationPending ? (
              <p className="text-sm text-label-secondary">
                Der Status folgt der Kündigung und wird hier nicht getrennt gewählt.
              </p>
            ) : (
              <label className="block space-y-1 text-sm sm:max-w-xs">
                <span className="text-label-secondary">Status</span>
                <select
                  className="eh-input w-full"
                  value={status === "notice" ? "active" : status}
                  disabled={!canEdit}
                  onChange={(e) => setStatus(e.target.value as ContractStatus)}
                >
                  <option value="active">Aktiv</option>
                  <option value="terminated">Beendet</option>
                </select>
              </label>
            )}
            <label className="block space-y-1 text-sm">
              <span className="text-label-secondary">Notizen</span>
              <textarea
                className="eh-input w-full min-h-[4.5rem]"
                value={notes}
                disabled={!canEdit}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Interne Hinweise zum Vertrag"
              />
            </label>
          </section>
        </form>
      </div>
      <RegisterVisitorModal
        open={visitorOpen}
        onClose={() => setVisitorOpen(false)}
        onCreated={onSaved}
        initialContractId={contract.id}
      />
    </Modal>
  );
}
